import React, { useRef, useMemo, useState, useEffect, useCallback } from 'react';
import { Button, Spinner, Nav, Tab } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faSync, faList, faChartLine, faBalanceScale, faHistory } from '@fortawesome/free-solid-svg-icons';
import { useHistory, useLocation } from 'react-router-dom';
import Header from '../../../component/header/header';
import MetersPageShell from './components/MetersPageShell';
import MeterCard from './components/MeterCard';
import MeterHealthStrip from './components/MeterHealthStrip';
import FilterBar from './components/FilterBar';
import MeterForm from './components/MeterForm';
import DeleteConfirmModal from './components/DeleteConfirmModal';
import MeterDashboardRouter, { hasWaterDashboard } from './routers/MeterDashboardRouter';
import WaterComparisonView from './components/comparison/WaterComparisonView';
import DowntimeReportModal from './components/DowntimeReportModal';
import { useMeters, useMeterTrends, useAllMeterAlerts } from './hooks/useMeters';
import { useURLFilters } from './utils/urlHelpers';
import { METER_FILTER_SCHEMA, filterMeters, latestFinancialYear } from './utils/meterFilters';
import { buildTrendConfig, groupTrendsByMeter } from './utils/meterTrends';
import { groupAlertsByMeter } from './utils/meterAlerts';
import { useFinancialYears, useSources } from '../../../hooks/useApiData';
import { usePermission } from '../../../hooks/usePermission';
import './styles/MetersList.css';
import { useCurrentUser } from '../../../hooks/useCurrentUser';

const MetersList = (props) => {
  const history = useHistory();
  const location = useLocation();
  const { filters, updateFilters, clearFilters } = useURLFilters(METER_FILTER_SCHEMA);
  const { data: meters = [], isLoading, error, refetch } = useMeters();
  const sources = useSources();
  const financialYears = useFinancialYears();
  const currentFinancialYear = useMemo(
    () => latestFinancialYear(financialYears),
    [financialYears]
  );

  const meterFormRef = useRef(null);
  const deleteModalRef = useRef(null);

  const [canViewList] = usePermission('iot-meters.view-list', 'IOT_METERS');
  const [canViewDashboard] = usePermission('iot-meters.view-dashboard', 'IOT_METERS');

  const [canCreateMeter] = usePermission('iot-meters.create-meter', 'IOT_METERS');
  const [canUpdateMeter] = usePermission('iot-meters.update-meter', 'IOT_METERS');
  const [canDeleteMeter] = usePermission('iot-meters.delete-meter', 'IOT_METERS');

  // Get active tab from URL or default to 'list'
  const queryParams = new URLSearchParams(location.search);
  const [activeTab, setActiveTab] = useState(queryParams.get('tab') || 'list');
  const [showDowntime, setShowDowntime] = useState(false);

  const currentUser = useCurrentUser();
  const companyId = currentUser.companyId;

  /**
   * The comparison compares water quantities, so it is offered only where the
   * water dashboard is — and behind the same permission, since it shows the
   * same figures in a different arrangement.
   */
  const canCompare = canViewDashboard && hasWaterDashboard(companyId);

  const availableTabs = useMemo(() => {
    const tabs = [];
    if (canViewList) tabs.push('list');
    if (canViewDashboard) tabs.push('dashboard');
    if (canCompare) tabs.push('comparison');
    return tabs;
  }, [canViewList, canViewDashboard, canCompare]);

  // Adjust active tab if current activeTab is not permitted
  useEffect(() => {
    if (availableTabs.length > 0 && !availableTabs.includes(activeTab)) {
      setActiveTab(availableTabs[0]);
    }
  }, [availableTabs, activeTab]);

  // Update URL when tab changes
  const handleTabChange = (key) => {
    setActiveTab(key);
    const params = new URLSearchParams(location.search);
    params.set('tab', key);
    history.push({ search: params.toString() });
  };

  /**
   * Location options for the filter bar. Ids are coerced to numbers so they match
   * both the meter's numeric sourceId and the numeric filter decoded from the URL.
   */
  const sourceOptions = useMemo(() =>
    sources.map(s => ({
      value: Number(s.id),
      label: s.unitCode || s.location?.area || 'Unknown',
      subLocations: (s.subLocation || []).map(sl => ({
        value: Number(sl.id),
        label: sl.subLocation
      }))
    })),
    [sources]
  );

  const financialYearOptions = useMemo(() => 
    financialYears.map(fy => ({
      value: fy.financial_year_value,
      label: fy.financial_year_value
    })), 
    [financialYears]
  );

  const filteredMeters = useMemo(
    () => filterMeters(meters, filters, currentFinancialYear),
    [meters, filters, currentFinancialYear]
  );

  /**
   * Fourteen days of daily consumption for every meter, fetched once and shared
   * across the grid rather than one request per card.
   */
  const trendConfig = useMemo(
    () => buildTrendConfig(meters, currentFinancialYear),
    [meters, currentFinancialYear]
  );
  const { data: trendData } = useMeterTrends(trendConfig);
  const trendsByMeter = useMemo(
    () => groupTrendsByMeter(trendData?.data || []),
    [trendData]
  );

  /**
   * Every meter's alert history, fetched once. Each card dates itself from
   * when its meter last returned to normal, which is what the details page
   * reports and what a reader means by "active since".
   */
  const { data: alertData } = useAllMeterAlerts();
  const alertsByMeter = useMemo(
    () => groupAlertsByMeter(alertData || []),
    [alertData]
  );

  const handleAddMeter = () => {
    meterFormRef.current?.show();
  };

  const handleEditMeter = useCallback((meter) => {
    meterFormRef.current?.show(meter);
  }, []);

  const handleDeleteMeter = useCallback((meter) => {
    deleteModalRef.current?.show(meter);
  }, []);

  const handleMeterClick = useCallback((meter) => {
    history.push(`/iot-meters/${meter.meterId}`);
  }, [history]);


  const handleSave = () => {
    refetch();
  };

  const handleDelete = () => {
    refetch();
  };

  return (
    <MetersPageShell className="im-root">
      <div style={{ position: "sticky", top: 0, zIndex: 999 }}><Header /></div>
    
      <div className="im-content">
        <div className="im-page-header">
          <div>
            <h1>IoT Meters</h1>
            <p>Manage and monitor your IoT meters</p>
          </div>
          {activeTab === 'list' && (
            <div className="im-page-actions">
              <Button variant="outline-secondary" onClick={() => setShowDowntime(true)}>
                <FontAwesomeIcon icon={faHistory} className="me-2" />
                Downtime report
              </Button>
              {canCreateMeter && (
                <Button variant="primary" onClick={handleAddMeter}>
                  <FontAwesomeIcon icon={faPlus} className="me-2" />
                  Add Meter
                </Button>
              )}
            </div>
          )}
        </div>

        <Tab.Container activeKey={activeTab} onSelect={handleTabChange}>
          <Nav variant="tabs" className="im-tabs mb-4">
            {canViewList && (
              <Nav.Item>
                <Nav.Link eventKey="list">
                  <FontAwesomeIcon icon={faList} className="me-2" />
                  Meters List
                </Nav.Link>
              </Nav.Item>
            )}
            {canViewDashboard && (
              <Nav.Item>
                <Nav.Link eventKey="dashboard">
                  <FontAwesomeIcon icon={faChartLine} className="me-2" />
                  Dashboard
                </Nav.Link>
              </Nav.Item>
            )}
            {canCompare && (
              <Nav.Item>
                <Nav.Link eventKey="comparison">
                  <FontAwesomeIcon icon={faBalanceScale} className="me-2" />
                  Comparison
                </Nav.Link>
              </Nav.Item>
            )}
            {/* Alert Rules, Alerts and Report Rules moved to
                Settings → Email notifications (centralized email UI). */}
          </Nav>

          <Tab.Content>
            {/* Meters List Tab */}
            {canViewList && (
              <Tab.Pane eventKey="list">
                {activeTab === 'list' && (
                  <>
                    <MeterHealthStrip
                      meters={meters}
                      health={filters.health}
                      onHealthChange={(health) => updateFilters({ health })}
                    />

                    <FilterBar
                      meters={meters}
                      filters={filters}
                      onFiltersChange={updateFilters}
                      onClearFilters={clearFilters}
                      currentFinancialYear={currentFinancialYear}
                      sources={sourceOptions}
                    />

                    {error && (
                      <div className="alert alert-danger" role="alert">
                        Failed to load meters. Please try again.
                        <Button 
                          variant="outline-danger" 
                          size="sm" 
                          className="ms-3"
                          onClick={() => refetch()}
                        >
                          <FontAwesomeIcon icon={faSync} className="me-1" /> Retry
                        </Button>
                      </div>
                    )}

                    <div className="meters-container">
                      {isLoading ? (
                        <div className="im-placeholder">
                          <Spinner animation="border" variant="primary" />
                          <p className="mt-3 mb-0">Loading meters&hellip;</p>
                        </div>
                      ) : filteredMeters.length > 0 ? (
                        <>
                          <div className="im-resultline">
                            <span>
                              Showing <b>{filteredMeters.length}</b> of{' '}
                              <b>{meters.length}</b> meters
                            </span>
                          </div>
                          <div className="im-grid">
                            {filteredMeters.map((meter, index) => (
                              <MeterCard
                                key={meter.meterId}
                                index={index}
                                meter={meter}
                                financialYear={currentFinancialYear}
                                trend={trendsByMeter[meter.meterName]}
                                alerts={alertsByMeter[meter.meterId]}
                                onEdit={handleEditMeter}
                                onDelete={handleDeleteMeter}
                                onClick={handleMeterClick}
                                canEdit={canUpdateMeter}
                                canDelete={canDeleteMeter}
                              />
                            ))}
                          </div>
                        </>
                      ) : (
                        <div className="im-placeholder">
                          <h4>No meters found</h4>
                          <p>
                            {meters.length === 0 
                              ? 'Get started by adding your first meter.'
                              : 'Try adjusting your filters to find meters.'
                            }
                          </p>
                          {meters.length === 0 ? (
                            canCreateMeter && (
                              <Button 
                                variant="primary" 
                                onClick={handleAddMeter}
                                className="mt-2"
                              >
                                <FontAwesomeIcon icon={faPlus} className="me-2" />
                                Add Your First Meter
                              </Button>
                            )
                          ) : (
                            <Button 
                              variant="outline-primary" 
                              onClick={clearFilters}
                              className="mt-2"
                            >
                              <FontAwesomeIcon icon={faSync} className="me-2" />
                              Clear Filters
                            </Button>
                          )}
                        </div>
                      )}
                    </div>
                  </>
                )}
              </Tab.Pane>
            )}

            {/* Dashboard Tab - Uses Router */}
            {canViewDashboard && (
              <Tab.Pane eventKey="dashboard">
                {activeTab === 'dashboard' && (
                  <MeterDashboardRouter
                    companyId={companyId}
                    meters={meters}
                    financialYearOptions={financialYearOptions}
                    sourceOptions={sourceOptions}
                    isLoading={isLoading}
                    onRefresh={refetch}
                  />
                )}
              </Tab.Pane>
            )}

            {/* Comparison Tab */}
            {canCompare && (
              <Tab.Pane eventKey="comparison">
                {activeTab === 'comparison' && (
                  <WaterComparisonView
                    meters={meters}
                    financialYearOptions={financialYearOptions}
                    sourceOptions={sourceOptions}
                    onRefresh={refetch}
                  />
                )}
              </Tab.Pane>
            )}

            {/* Alert Rules / Alerts / Report Rules now live under
                Settings → Email notifications. */}
          </Tab.Content>
        </Tab.Container>
      </div>

      <MeterForm
        ref={meterFormRef}
        onSave={handleSave}
        sources={sourceOptions}
      />

      <DeleteConfirmModal
        ref={deleteModalRef}
        onDelete={handleDelete}
      />
      <DowntimeReportModal
        show={showDowntime}
        onHide={() => setShowDowntime(false)}
        meters={meters}
      />
    </MetersPageShell>
  );
};

export default MetersList;
